import { useState, useMemo } from "react";
import { Play, Loader2, Code2, FormInput } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import type { CaseContextField } from "@/types/decision-editor";
import type { DecisionEvaluationResult } from "@/types/decision-definition-schema";
import { TraceViewer } from "./TraceViewer";
import { api } from "@/lib/api-client";

interface DecisionTesterProps {
  decisionId: string;
  decisionJson?: string;
  caseContextSchema: CaseContextField[];
}

export function DecisionTester({
  decisionId,
  decisionJson,
  caseContextSchema,
}: DecisionTesterProps) {
  const [inputMode, setInputMode] = useState<"form" | "json">("form");
  const [caseContext, setCaseContext] = useState<Record<string, unknown>>({});
  const [rawJson, setRawJson] = useState("{}");
  const [testResult, setTestResult] = useState<DecisionEvaluationResult | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleFieldChange = (path: string, value: unknown) => {
    const keys = path.split(".");
    const newContext = { ...caseContext };
    let current: Record<string, unknown> = newContext;

    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]] || typeof current[keys[i]] !== "object") {
        current[keys[i]] = {};
      }
      current = current[keys[i]] as Record<string, unknown>;
    }

    current[keys[keys.length - 1]] = value;
    setCaseContext(newContext);
  };

  const getFieldValue = (path: string): unknown => {
    const keys = path.split(".");
    let current: unknown = caseContext;
    for (const key of keys) {
      if (current === undefined || current === null) return "";
      current = (current as Record<string, unknown>)[key];
    }
    return current ?? "";
  };

  const handleRunTest = async () => {
    setIsLoading(true);
    setTestResult(null);
    setError(null);

    try {
      const facts =
        inputMode === "json" ? JSON.parse(rawJson) : caseContext;

      const body: Record<string, unknown> = {
        facts,
        trace: true,
      };

      if (decisionJson) {
        body.decision_definition = JSON.parse(decisionJson);
      }

      const response = await api.post(
        `/decisions/${decisionId}/test`,
        body
      );

      setTestResult(response.data as DecisionEvaluationResult);
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError("Invalid JSON in input");
      } else {
        setError(err instanceof Error ? err.message : "Unknown error");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Input Builder */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Test Input</CardTitle>
            <Tabs
              value={inputMode}
              onValueChange={(v) => setInputMode(v as "form" | "json")}
            >
              <TabsList className="h-7">
                <TabsTrigger value="form" className="h-5 px-2 text-xs gap-1">
                  <FormInput size={11} />
                  Form
                </TabsTrigger>
                <TabsTrigger value="json" className="h-5 px-2 text-xs gap-1">
                  <Code2 size={11} />
                  JSON
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {inputMode === "form" &&
            caseContextSchema.map((field) => (
              <div key={field.path}>
                <Label className="text-xs">
                  {field.label}
                  <span className="text-muted-foreground ml-1 font-normal">
                    ({field.path})
                  </span>
                </Label>

                {field.type === "string" && (
                  <Input
                    className="h-7 text-xs"
                    value={String(getFieldValue(field.path))}
                    onChange={(e) =>
                      handleFieldChange(field.path, e.target.value)
                    }
                    placeholder={field.description}
                  />
                )}

                {field.type === "number" && (
                  <Input
                    className="h-7 text-xs"
                    type="number"
                    value={String(getFieldValue(field.path))}
                    onChange={(e) =>
                      handleFieldChange(field.path, Number(e.target.value))
                    }
                    placeholder={field.description}
                  />
                )}

                {field.type === "boolean" && (
                  <select
                    className="mt-1 block w-full rounded-md border border-border px-2 py-1 text-xs"
                    value={String(getFieldValue(field.path))}
                    onChange={(e) =>
                      handleFieldChange(
                        field.path,
                        e.target.value === "true"
                      )
                    }
                  >
                    <option value="">Select...</option>
                    <option value="true">True</option>
                    <option value="false">False</option>
                  </select>
                )}

                {field.type === "date" && (
                  <Input
                    className="h-7 text-xs"
                    type="date"
                    value={String(getFieldValue(field.path))}
                    onChange={(e) =>
                      handleFieldChange(field.path, e.target.value)
                    }
                  />
                )}

                {(field.type === "array" || field.type === "object") && (
                  <Textarea
                    className="text-xs font-mono"
                    value={JSON.stringify(
                      getFieldValue(field.path) || (field.type === "array" ? [] : {}),
                      null,
                      2
                    )}
                    onChange={(e) => {
                      try {
                        handleFieldChange(
                          field.path,
                          JSON.parse(e.target.value)
                        );
                      } catch {
                        /* invalid JSON, ignore */
                      }
                    }}
                    rows={3}
                  />
                )}
              </div>
            ))}

          {inputMode === "form" && caseContextSchema.length === 0 && (
            <p className="text-xs text-muted-foreground py-4 text-center">
              No input fields detected. Add fact_ref nodes to the decision
              expression, or use JSON mode.
            </p>
          )}

          {inputMode === "json" && (
            <Textarea
              className="text-xs font-mono min-h-[200px]"
              value={rawJson}
              onChange={(e) => setRawJson(e.target.value)}
              placeholder='{ "applicant": { "income": 120000, "creditScore": 750 } }'
            />
          )}
        </CardContent>
      </Card>

      {/* Run */}
      <div className="flex justify-end">
        <Button onClick={handleRunTest} disabled={isLoading} size="sm">
          {isLoading ? (
            <>
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              Evaluating...
            </>
          ) : (
            <>
              <Play className="h-3.5 w-3.5 mr-1.5" />
              Run Test
            </>
          )}
        </Button>
      </div>

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Result */}
      {testResult && <TestResultView result={testResult} />}
    </div>
  );
}

// =============================================================================
// Test Result Display
// =============================================================================

function TestResultView({
  result,
}: {
  result: DecisionEvaluationResult;
}) {
  const triStateColor =
    result.rootTriState === "true"
      ? "text-green-700 bg-green-50 border-green-200"
      : result.rootTriState === "false"
        ? "text-red-700 bg-red-50 border-red-200"
        : "text-amber-700 bg-amber-50 border-amber-200";

  return (
    <div className="space-y-3">
      {/* Summary */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Result</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Badge className={triStateColor}>
              {result.rootTriState?.toUpperCase() || "—"}
            </Badge>
            {result.outcome && (
              <Badge variant="outline">{result.outcome}</Badge>
            )}
            {result.approved !== undefined && (
              <Badge
                variant={result.approved ? "default" : "destructive"}
              >
                {result.approved ? "Approved" : "Not Approved"}
              </Badge>
            )}
            {result.score !== undefined && (
              <Badge variant="secondary">Score: {result.score}</Badge>
            )}
            {result.band && (
              <Badge variant="secondary">Band: {result.band}</Badge>
            )}
          </div>

          {/* Reasons */}
          {result.reasons && result.reasons.length > 0 && (
            <div className="mb-2">
              <Label className="text-xs">Reasons</Label>
              <ul className="text-xs space-y-0.5 mt-1">
                {result.reasons.map((r, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <code className="text-[10px] bg-muted px-1 rounded">
                      {r.code}
                    </code>
                    <span>{r.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Flags */}
          {result.flags && result.flags.length > 0 && (
            <div className="mb-2">
              <Label className="text-xs">Flags</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {result.flags.map((f, i) => (
                  <Badge key={i} variant="outline" className="text-[10px]">
                    {f.label || f.code}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Missing Facts */}
          {result.missingFacts && result.missingFacts.length > 0 && (
            <div className="mb-2">
              <Label className="text-xs text-amber-700">Missing Facts</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {result.missingFacts.map((f, i) => (
                  <Badge
                    key={i}
                    variant="outline"
                    className="text-[10px] border-amber-300 text-amber-700"
                  >
                    {f}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Obligations */}
          {result.obligations && result.obligations.length > 0 && (
            <div>
              <Label className="text-xs">Obligations</Label>
              <ul className="text-xs space-y-0.5 mt-1">
                {result.obligations.map((o, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className="text-[10px]"
                    >
                      {o.type}
                    </Badge>
                    <span>{o.title}</span>
                    {o.blocking && (
                      <Badge variant="destructive" className="text-[10px]">
                        blocking
                      </Badge>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Trace */}
      {result.trace && result.trace.length > 0 && (
        <TraceViewer trace={result.trace} />
      )}

      {/* Raw JSON */}
      <details className="text-xs">
        <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
          Raw JSON Response
        </summary>
        <pre className="bg-muted rounded-md p-3 mt-1 overflow-auto max-h-[300px]">
          <code>{JSON.stringify(result, null, 2)}</code>
        </pre>
      </details>
    </div>
  );
}
